// src/components/settings/ApiAccessTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Key, Plus, Copy, Eye, EyeOff, Trash2, AlertTriangle, 
  CheckCircle, Clock, Activity, Shield, ExternalLink,
  RefreshCw, XCircle, Info, Code, ChevronDown, ChevronUp
} from 'lucide-react';
import { useBusinessContext } from '../../contexts/BusinessContext';
import apiKeysService from '../../services/apiKeysService';
import Modal from '../Modal';

// Available scopes with descriptions
const AVAILABLE_SCOPES = {
  'customers:read': 'Read customer data',
  'customers:write': 'Create and update customers',
  'jobs:read': 'Read job data',
  'jobs:write': 'Create and update jobs',
  'invoices:read': 'Read invoice data',
  'invoices:write': 'Create invoices',
  'servicetypes:read': 'Read service types',
};

const ApiAccessTab = ({ showToast }) => {
  const { canAccessFeature, subscriptionPlan } = useBusinessContext();
  
  // State
  const [keys, setKeys] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [newKeyData, setNewKeyData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopes: [],
    expiresAt: '',
    useAllScopes: true,
  });
  const [revokeReason, setRevokeReason] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Copy state
  const [copied, setCopied] = useState(false);
  
  // Docs expanded state
  const [docsExpanded, setDocsExpanded] = useState(false);

  // Check if user has access
  const hasAccess = canAccessFeature('api-access');

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [keysResponse, statsResponse] = await Promise.all([
        apiKeysService.getAll(),
        apiKeysService.getUsageStats(),
      ]);
      
      setKeys(keysResponse.data);
      setUsageStats(statsResponse.data);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Please enter a name for the API key', 'error');
      return;
    }
    
    try {
      setCreating(true);
      
      const requestData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        scopes: formData.useAllScopes ? null : formData.scopes,
        expiresAt: formData.expiresAt || null,
      };
      
      const response = await apiKeysService.create(requestData);
      
      // Show the new key (only time it's visible!)
      setNewKeyData(response.data);
      setShowCreateModal(false);
      setShowKeyModal(true);
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        scopes: [],
        expiresAt: '',
        useAllScopes: true,
      });
      
      // Reload keys
      await loadData();
      
      showToast('API key created successfully!');
    } catch (err) {
      console.error('Error creating API key:', err);
      showToast(err.response?.data?.error || 'Failed to create API key', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!selectedKey) return;
    
    try {
      await apiKeysService.revoke(selectedKey.id, revokeReason || null);
      
      setShowRevokeModal(false);
      setSelectedKey(null);
      setRevokeReason('');
      
      await loadData();
      showToast('API key revoked');
    } catch (err) {
      console.error('Error revoking API key:', err);
      showToast(err.response?.data?.error || 'Failed to revoke API key', 'error');
    }
  };

  const handleDeleteKey = async () => {
    if (!selectedKey) return;
    
    try {
      await apiKeysService.delete(selectedKey.id);
      
      setShowDeleteModal(false);
      setSelectedKey(null);
      
      await loadData();
      showToast('API key deleted permanently');
    } catch (err) {
      console.error('Error deleting API key:', err);
      showToast(err.response?.data?.error || 'Failed to delete API key', 'error');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleScope = (scope) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // If user doesn't have access, show upgrade prompt
  if (!hasAccess) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Key className="h-8 w-8 text-purple-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">API Access</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Connect your external applications and automate workflows with our REST API.
            API access is available on the Enterprise plan.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium">
            <Shield size={16} />
            Enterprise Feature
          </div>
          <div className="mt-6">
            <a
              href="/subscribe"
              className="btn btn-primary"
            >
              Upgrade to Enterprise
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="btn btn-primary">
            <RefreshCw size={16} className="mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const activeKeys = keys.filter(k => k.isActive);
  const revokedKeys = keys.filter(k => !k.isActive);

  return (
    <div className="space-y-6">
      {/* Usage Overview */}
      {usageStats && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">API Usage</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Active Keys</p>
              <p className="text-2xl font-bold text-gray-900">{usageStats.activeKeys}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Requests Today</p>
              <p className="text-2xl font-bold text-gray-900">{usageStats.requestsToday.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{usageStats.requestsThisMonth.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Monthly Limit</p>
              <p className="text-2xl font-bold text-gray-900">{usageStats.monthlyLimit.toLocaleString()}</p>
            </div>
          </div>
          
          {/* Usage bar */}
          <div className="mb-2">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Monthly Usage</span>
              <span>{usageStats.usagePercentage.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  usageStats.usagePercentage > 90 ? 'bg-red-500' :
                  usageStats.usagePercentage > 75 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(usageStats.usagePercentage, 100)}%` }}
              />
            </div>
          </div>
          
          {usageStats.usagePercentage > 75 && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${
              usageStats.usagePercentage > 90 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
            }`}>
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                {usageStats.usagePercentage > 90 
                  ? 'You\'re approaching your monthly API limit. Consider optimizing your integration or contact support.'
                  : 'You\'ve used over 75% of your monthly API requests.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* API Keys List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">API Keys</h3>
            <p className="text-sm text-gray-600">Manage your API keys for external integrations</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <Plus size={16} className="mr-2" />
            Create Key
          </button>
        </div>

        {activeKeys.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No API Keys</h4>
            <p className="text-gray-600 mb-4">Create your first API key to get started with the API.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <Plus size={16} className="mr-2" />
              Create Your First Key
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {activeKeys.map((key) => (
              <div key={key.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900">{key.name}</h4>
                      {key.expiresAt && new Date(key.expiresAt) < new Date() && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          Expired
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                        {key.displayKey}
                      </code>
                    </div>
                    
                    {key.description && (
                      <p className="text-sm text-gray-600 mb-2">{key.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        Created {formatDate(key.createdAt)}
                      </span>
                      {key.lastUsedAt && (
                        <span className="flex items-center gap-1">
                          <Activity size={12} />
                          Last used {formatDateTime(key.lastUsedAt)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Activity size={12} />
                        {key.requestsThisMonth.toLocaleString()} requests this month
                      </span>
                      {key.expiresAt && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          Expires {formatDate(key.expiresAt)}
                        </span>
                      )}
                    </div>
                    
                    {key.scopes && key.scopes.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {key.scopes.map(scope => (
                          <span key={scope} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                            {scope}
                          </span>
                        ))}
                      </div>
                    )}
                    {!key.scopes && (
                      <div className="mt-2">
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                          Full Access
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => {
                      setSelectedKey(key);
                      setShowRevokeModal(true);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Revoke Key"
                  >
                    <XCircle size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Revoked Keys */}
        {revokedKeys.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Revoked Keys</h4>
            <div className="space-y-2">
              {revokedKeys.map((key) => (
                <div key={key.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-500 line-through">{key.name}</span>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          Revoked
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Revoked {formatDateTime(key.revokedAt)}
                        {key.revokedReason && ` — ${key.revokedReason}`}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedKey(key);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Permanently"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Start Documentation */}
      <div className="card">
        <button
          onClick={() => setDocsExpanded(!docsExpanded)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Code className="h-5 w-5 text-gray-600" />
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">Quick Start Guide</h3>
              <p className="text-sm text-gray-600">Learn how to use the API</p>
            </div>
          </div>
          {docsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {docsExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Authentication</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Include your API key in the <code className="bg-gray-100 px-1 rounded">X-API-Key</code> header:
                </p>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`curl -X GET "https://api.suddyflow.com/v1/customers" \\
  -H "X-API-Key: sf_live_your_api_key_here"`}
                </pre>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Example: Get Customers</h4>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`// JavaScript / Node.js
const response = await fetch('https://api.suddyflow.com/v1/customers', {
  headers: {
    'X-API-Key': 'sf_live_your_api_key_here',
    'Content-Type': 'application/json'
  }
});

const customers = await response.json();
console.log(customers);`}
                </pre>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Available Endpoints</h4>
                <div className="grid gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-mono text-xs">GET</span>
                    <code className="text-gray-600">/v1/customers</code>
                    <span className="text-gray-400">— List all customers</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono text-xs">POST</span>
                    <code className="text-gray-600">/v1/customers</code>
                    <span className="text-gray-400">— Create a customer</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-mono text-xs">GET</span>
                    <code className="text-gray-600">/v1/jobs</code>
                    <span className="text-gray-400">— List all jobs</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono text-xs">POST</span>
                    <code className="text-gray-600">/v1/jobs</code>
                    <span className="text-gray-400">— Create a job</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-mono text-xs">GET</span>
                    <code className="text-gray-600">/v1/invoices</code>
                    <span className="text-gray-400">— List all invoices</span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  Full API documentation with all endpoints, request/response schemas, and more examples coming soon.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Key Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create API Key"
      >
        <form onSubmit={handleCreateKey} className="space-y-4">
          <div>
            <label className="label">Key Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., Production, SuddyBuckets Integration"
              maxLength={100}
              required
            />
            <p className="text-xs text-gray-500 mt-1">A friendly name to identify this key</p>
          </div>
          
          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input"
              rows={2}
              placeholder="What is this key used for?"
              maxLength={500}
            />
          </div>
          
          <div>
            <label className="label">Permissions</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.useAllScopes}
                  onChange={(e) => setFormData({ ...formData, useAllScopes: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600"
                />
                <span className="text-sm">Full Access (all permissions)</span>
              </label>
              
              {!formData.useAllScopes && (
                <div className="ml-6 space-y-2 p-3 bg-gray-50 rounded-lg">
                  {Object.entries(AVAILABLE_SCOPES).map(([scope, description]) => (
                    <label key={scope} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.scopes.includes(scope)}
                        onChange={() => toggleScope(scope)}
                        className="rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm">
                        <code className="bg-gray-200 px-1 rounded text-xs">{scope}</code>
                        <span className="text-gray-600 ml-2">— {description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="label">Expiration Date (Optional)</label>
            <input
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="input"
              min={new Date().toISOString().split('T')[0]}
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
          </div>
          
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertTriangle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              The API key will only be shown once after creation. Make sure to copy it immediately.
            </p>
          </div>
          
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="btn btn-primary flex-1"
            >
              {creating ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Show New Key Modal */}
      <Modal
        isOpen={showKeyModal}
        onClose={() => {
          setShowKeyModal(false);
          setNewKeyData(null);
        }}
        title="API Key Created"
      >
        {newKeyData && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-green-900">Key Created Successfully!</h4>
                <p className="text-sm text-green-700 mt-1">
                  Copy your API key now. You won't be able to see it again.
                </p>
              </div>
            </div>
            
            <div>
              <label className="label">Your API Key</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newKeyData.apiKey}
                  readOnly
                  className="input font-mono text-sm flex-1"
                />
                <button
                  onClick={() => copyToClipboard(newKeyData.apiKey)}
                  className={`btn ${copied ? 'btn-success' : 'btn-secondary'}`}
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">
                <strong>Important:</strong> This is the only time you'll see this key. 
                Store it securely and never share it publicly.
              </p>
            </div>
            
            <button
              onClick={() => {
                setShowKeyModal(false);
                setNewKeyData(null);
              }}
              className="btn btn-primary w-full"
            >
              I've Copied My Key
            </button>
          </div>
        )}
      </Modal>

      {/* Revoke Key Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setSelectedKey(null);
          setRevokeReason('');
        }}
        title="Revoke API Key"
      >
        {selectedKey && (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-yellow-900">Are you sure?</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Revoking this key will immediately stop all API access using it.
                  Any integrations using this key will stop working.
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Key to revoke:</p>
              <p className="font-medium text-gray-900">{selectedKey.name}</p>
              <code className="text-xs text-gray-500">{selectedKey.displayKey}</code>
            </div>
            
            <div>
              <label className="label">Reason for Revoking (Optional)</label>
              <input
                type="text"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                className="input"
                placeholder="e.g., Key compromised, no longer needed"
                maxLength={500}
              />
            </div>
            
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setSelectedKey(null);
                  setRevokeReason('');
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeKey}
                className="btn bg-red-600 hover:bg-red-700 text-white flex-1"
              >
                Revoke Key
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Key Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedKey(null);
        }}
        title="Delete API Key Permanently"
      >
        {selectedKey && (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-red-900">This action cannot be undone</h4>
                <p className="text-sm text-red-700 mt-1">
                  Permanently deleting this key will also delete all usage logs associated with it.
                </p>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Key to delete:</p>
              <p className="font-medium text-gray-900">{selectedKey.name}</p>
            </div>
            
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedKey(null);
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteKey}
                className="btn bg-red-600 hover:bg-red-700 text-white flex-1"
              >
                <Trash2 size={16} className="mr-2" />
                Delete Permanently
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApiAccessTab;